import {Form as RouterForm, useParams} from 'react-router-dom';
import {useAppSelector} from '../../store/store';
import {Status} from '../../enums/status';
import {CenteredSpinner} from '../../components/spinner/centered_spinner';
import {Main} from '../../components/main/main.tsx';
import mainStyles from '../../components/main/main.module.scss';
import {Page} from '../../components/page/page.tsx';
import {VehicleBrandModel} from '../../components/vehicle/vehicle_brand_model.tsx';
import {__} from '../../support/lang/translations.ts';
import {Tabs} from '../../components/tabs/tabs.tsx';
import {Details} from './tabs/details.tsx';
import {Mileage} from './tabs/mileage.tsx';
import {FormFooter} from '../../components/form_footer/form_footer.tsx';
import {Button} from '../../components/button/button.tsx';
import {ButtonPosition} from '../../enums/button_position.ts';
import {VehicleLogo, VehicleLogoSize} from '../../components/vehicle/vehicle_logo.tsx';
import {Identifier} from '../../components/identifier/identifier.tsx';
import {IdentifierSize} from '../../support/enums/identifier_size.ts';
import {brandLogo} from '../../support/brand_logo.ts';
import styles from '../../pages/vehicle_details/vehicle_details.module.scss';
import {FlexColumn} from '../../components/utility/flex_column.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {FlexRow} from '../../components/utility/flex_row.tsx';
import {ErrorPage} from '../error.tsx';
import {useIsNavigatingOrPending} from '../../support/use_is_navigating_or_pending.ts';

export function VehicleDetails() {
    const params = useParams<'vehicleId'>();
    const vehicleId = parseInt(params.vehicleId ?? '-1', 10);
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const isLoading = useIsNavigatingOrPending((state) => state.customerInfo.status);

    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    const vehicle = customerInfo.me?.customer?.vehicles?.find((v) => v?.id === vehicleId);
    if (!vehicle) {
        return <ErrorPage code={404} />;
    }

    return (
        <Main className={mainStyles.bg_white}>
            <div className={styles.vehicle_details}>
                <nav className={styles.vehicle_details__nav}>
                    <div className={styles.title__buttons}>
                        <FlexRow>
                            <FlexColumn alignment={Alignment.Start}>
                                <Button
                                    to="/vehicles"
                                    position={ButtonPosition.Right}
                                    noBorder={true}
                                    preventScrollReset={true}
                                >
                                    <i className="fa-light fa-chevron-left" />
                                    {__('vehicles.buttons.back')}
                                </Button>
                            </FlexColumn>
                        </FlexRow>
                    </div>
                </nav>
                <Page>
                    <VehicleLogo
                        src={brandLogo(vehicle.brand)}
                        alt={vehicle.brand ?? 'auto'}
                        size={VehicleLogoSize.Large}
                    />

                    <VehicleBrandModel brand={vehicle.brand ?? ''} model={vehicle.model ?? ''}>
                        <Identifier
                            identifier={vehicle.identifiers?.find((id) => id?.type === 'license_plate')?.value ?? ''}
                            size={IdentifierSize.Large}
                        />
                    </VehicleBrandModel>

                    <RouterForm method="post">
                        <input name="id" type="hidden" value={vehicleId} />
                        <Tabs
                            disabled={isLoading}
                            name="vehicle"
                            tabs={[
                                {
                                    id: 'general',
                                    link: <span>{__('vehicles.tabs.general')}</span>,
                                    child: <Details />,
                                },
                                {
                                    id: 'mileage',
                                    link: <span>{__('vehicles.tabs.mileage')}</span>,
                                    child: <Mileage />,
                                },
                            ]}
                        />
                        <FormFooter gray={true}>
                            <Button disabled={isLoading} loading={isLoading} position={ButtonPosition.Right}>
                                <i className={isLoading ? 'fa-light fa-refresh' : 'fa-light fa-check'} />
                                <span>{__('profile.buttons.save')}</span>
                            </Button>
                        </FormFooter>
                    </RouterForm>
                </Page>
            </div>
        </Main>
    );
}
